import { Component, OnInit, ViewChild, AfterViewInit, ElementRef, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { LayoutService } from '../core/service/layout.service';
import { AccountService } from '../accounts/service/account.service';
import { NotificationType } from '../accounts/model/NotificationType';
import { CustomErrorHandler } from '../core/service/CustomErrorHandler';
import { CaseSplitPipe } from '../shared/pipe/CaseSplit.pipe';
import { Router } from '@angular/router';

enum SearchOption {
  RegNumber,
  PreEntryNumber,
  OwnerNumber
}

interface NotificationListItem {
  notificationDate: string;
  dateOfNotification?: string;
  recipients: Array<{ gPhCId: string; notificationId: string }>;
  type: NotificationType;
}

@Component({
    templateUrl: './notification.component.html',
    styleUrls: ['../revalidation/list.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class NotificationComponent implements AfterViewInit, OnDestroy, OnInit {

  NotificationType = NotificationType;
  loading = false;
  searchVisible = true;
  searchCriteria = {
    regNumber: '',
    preEntryNumber: '',
    ownerNumber:''
  };

  SearchOption = SearchOption;
  searchBy = SearchOption.RegNumber;
  notifications: NotificationListItem[] = [];
  notificationOwner: string | null = null;
  owner: { ownerId: string; ownerName: string; ownerNumber: string } = {
    ownerId: '',
    ownerName: '',
    ownerNumber: ''
  };
  exitToDetailsPage: boolean = false;

  @ViewChild('regNumber') regNumberRef!: ElementRef;

  constructor(
    public layout: LayoutService,
    public account: AccountService,
    public error: CustomErrorHandler,
    private router: Router
  ) { }

  ngOnInit(): void {
    if(this.account.ownerNumberToSearch) {
      this.searchBy = SearchOption.OwnerNumber;
      this.owner.ownerNumber =  this.account.ownerNumberToSearch;
      this.fetchNotifications(this.owner.ownerNumber);
    }
  }

  fetchNotifications(ownerNumber: string | null): void {
    if(ownerNumber) {
      this.loading = true;
      this.account.getPremisesNotifications(ownerNumber).subscribe(
          notificationsData => {
            this.notificationOwner = notificationsData.recipient.ownerName;
            this.notifications = notificationsData.notifications.data;
            this.searchCriteria.ownerNumber = ownerNumber;
            this.setNotificationsDetails(true,{owner: { ownerId:notificationsData.recipient.ownerId, ownerName: notificationsData.recipient.ownerName, ownerNumber:notificationsData.recipient.ownerNumber}, notifications: notificationsData.notifications.data});
            this.loading = false;
          },
          error => {
            this.setNotificationsDetails(false,{owner: { ownerId: null, ownerName: null, ownerNumber:null},notifications:[]});
            this.loading = false;
          }
      );
    }
  }

  search() {
    if (this.loading) {
      return;
    }
    if (
      !this.searchCriteria.regNumber?.trim() &&
      !this.searchCriteria.preEntryNumber?.trim() &&
      !this.searchCriteria.ownerNumber?.trim()
    ) {
      return;
    }
    this.loading = true;
    switch (this.searchBy) {
      case SearchOption.PreEntryNumber:
        this.account.getNotificationsByPreEntryNumber(this.searchCriteria.preEntryNumber).subscribe(notificationsData => {
          this.notificationOwner = notificationsData.recipient.fullName;
          this.notifications = notificationsData.notifications.data;
          this.loading = false;
        },
        error => {
          this.notifications = [];
          this.loading = false;
        }
      );
        break;
      case SearchOption.RegNumber:
        this.account.getNotificationsByRegNumber(this.searchCriteria.regNumber).subscribe(
          notificationsData => {
            this.notificationOwner = notificationsData.recipient.fullName;
            this.notifications = notificationsData.notifications.data;
            this.loading = false;
          },
          error => {
            this.notifications = [];
            this.loading = false;
          }
        );
        break;
      case SearchOption.OwnerNumber:
        this.account.ownerNumber$.next(this.searchCriteria.ownerNumber);
        this.fetchNotifications(this.searchCriteria.ownerNumber);
        break;
      default:
        throw new Error('Neither pre-entry number nor registration number selected');
    }
  }

  setNotificationsDetails(status: boolean, details: {
    owner: { ownerId: string | null; ownerName: string | null; ownerNumber: string | null };
    notifications: NotificationListItem[];
  }): void {
    if(status) {
      this.owner.ownerId = details.owner.ownerId ?? '';
      this.owner.ownerName = details.owner.ownerName ?? '';
      this.owner.ownerNumber = details.owner.ownerNumber ?? '';
      this.notifications = details.notifications;
    } else {
      this.owner.ownerName = '';
      this.notifications = [];
    }
    
  }

  ngAfterViewInit() {
    this.setFocus();
  }


  get searchByRegNumber() {
    return this.searchBy === SearchOption.RegNumber;
  }

  get searchByPreEntryNumber() {
    return this.searchBy === SearchOption.PreEntryNumber;
  }

  get searchByOwnerNumber() {
    return this.searchBy === SearchOption.OwnerNumber;
  }

  getNotificationType(notificationType: NotificationType) {
  
      const caseSplitPipe = new CaseSplitPipe();
  
      if(notificationType) {
      if (notificationType === NotificationType.RenewalWindowOpeningForPharmacyForCardBACS) {
        return 'Renewal window opening for pharmacy for Card/BACS';
      }
      if (notificationType === NotificationType.PharmacyRenewalDeclarationReminderForDD) {
        return 'Pharmacy renewal declaration reminder for DD';
      }
      if (notificationType === NotificationType.PharmacyRenewalDeclarationMissedForDD) {
        return 'Pharmacy renewal declaration missed for DD';
      }
      if (notificationType === NotificationType.PharmacyRenewalPaymentSubmittedForBACS) {
        return 'Pharmacy renewal payment submitted for BACS';
      }
        return caseSplitPipe.transform(NotificationType[notificationType]);
      }
      return '';
    }

  viewNotificationDetails(rowNumber: number): void {
    let updatedPremisesNotification = {ownerNumber: this.owner.ownerNumber,ownerId: this.owner.ownerId,notifications: this.notifications, row: rowNumber};
    this.account.premisesNotificationType$.next(updatedPremisesNotification);

    this.exitToDetailsPage = true;
    this.router.navigate(['/notification/details']);
  }

  setFocus() {
    this.notifications = [];
    this.notificationOwner = null;
    this.searchCriteria.ownerNumber = '';
    this.searchCriteria.preEntryNumber = '';
    this.searchCriteria.regNumber = '';
    if (this.searchVisible) {
      setTimeout(() => {
        this.regNumberRef.nativeElement.focus();
      });
    }
  }

  showSearch() {
    this.searchVisible = !this.searchVisible;
  }

  clearSearch() {
    this.searchCriteria.regNumber = '';
    this.searchCriteria.preEntryNumber = '';
    this.searchCriteria.ownerNumber = '';
    this.notifications = [];
    this.loading = false;
  }

  ngOnDestroy(): void {
    if(this.exitToDetailsPage) this.account.ownerNumber$.next(this.owner.ownerNumber);
    else this.account.ownerNumber$.next(null);
  }
}
